import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type SigningCollectionReviewer,
  type SigningReview,
} from 'xosign-types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type UploadedFile } from '@/types/document';
import useApi from '@/hooks/useApi';
import { stringToBigInt } from '@/utils/bigInt';

type ReviewContextType = {
  documents: UploadedFile[];
  loadAllData: () => void;
  overallLoading: boolean;
  review?: SigningReview;
  reviewerStatusId?: string;
  setDocuments: Dispatch<SetStateAction<UploadedFile[]>>;
  visitor?: SigningCollectionReviewer;
};

const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

export const ReviewProvider = ({ children }: { children: ReactNode }) => {
  const { reviewerStatusId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language ?? 'en';

  const [review, setReview] = useState<SigningReview>();
  // We need to store the documents separately to add FE object keys
  const [documents, setDocuments] = useState<UploadedFile[]>([]);
  const [visitor, setVisitor] = useState<SigningCollectionReviewer>();

  const [loadReview, { loading: loadingReview }] = useApi('review.get');

  const loadAllData = () => {
    if (!reviewerStatusId) {
      return;
    }
    void (async () => {
      const reviewResult = await loadReview({ reviewerStatusId });

      if (reviewResult?.response?.success) {
        const reviewData = reviewResult.response.data;
        setReview(reviewData);

        setDocuments(
          reviewData.signing.documents.map((document) => ({
            id: document.id,
            progress: 100,
            status: 'success' as const,
            fileMeta: {
              name: document.filename,
              size: stringToBigInt(document.byteSize),
              pageCount: document.pageCount,
            },
          }))
        );

        if (reviewData.reviewers.length > 0) {
          const foundVisitor = reviewData.reviewers.find(
            (reviewer) => reviewer.reviewer.id === reviewData.reviewerId
          );

          if (foundVisitor) {
            setVisitor(foundVisitor.reviewer);
          }
        }
      }
    })();
  };

  // `loadAllData` is a fresh closure each render — listing it as a dep would
  // cause an infinite re-fire. Read it via a ref instead.
  const loadAllDataRef = useRef(loadAllData);
  useEffect(() => {
    loadAllDataRef.current = loadAllData;
  });

  // initially load the token and validate by response
  useEffect(() => {
    const token = searchParams.get('key');

    if (!reviewerStatusId || (!token && !sessionStorage.getItem('token'))) {
      void navigate(
        `https://www.swipesign.io/${lang !== 'en' ? `${lang}/` : ''}`,
        {
          replace: true,
        }
      );
    } else {
      if (token) {
        localStorage.setItem('token', token);
        sessionStorage.setItem('token', token);
      }

      loadAllDataRef.current();
    }
  }, [lang, navigate, reviewerStatusId, searchParams]);

  const contextValue = {
    documents,
    loadAllData,
    overallLoading: !!loadingReview,
    review,
    reviewerStatusId,
    setDocuments,
    visitor,
  };

  return (
    <ReviewContext.Provider value={contextValue}>
      {children}
    </ReviewContext.Provider>
  );
};

const useReview = (): ReviewContextType => {
  const ctx = useContext(ReviewContext);

  if (!ctx) {
    throw new Error('useReview must be used within ReviewProvider');
  }

  return ctx;
};

export default useReview;
